import React from "react";

import axios from "axios";

import {
  Container,
  Grid,
  LinearProgress,
  FormLabel,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  InputLabel,
  Select,
} from "@material-ui/core";

import {
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Text,
  Tooltip,
  ComposedChart,
  Scatter,
  ReferenceLine,
} from "recharts";

import { format } from "date-fns";
import Sticky from "react-stickynode";

import { COLORS } from "../constants/colors";
import { italyDevUrl } from "../constants/urls";

class Charts extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      infections: false,
      newDeaths: true,
      data: [],
      scaleValue: "Linear",
      frequencyValue: "weekly",
      scenarios: [],
      selectedScenarios: {},
      loading: false,
      scatter: [],
      scenarioIDs: {},
      metricIDs: {},
    };
  }

  async componentDidMount() {
    const { state, scenarios } = this.props;
    const [
      dataResponse,
      scenariosResponse,
      metricsResponse,
    ] = await Promise.all([
      axios.get(
        `${italyDevUrl}data?state=${state}&frequency=weekly&selectedScenarios=["Lockdown kept indefinitely"]`
      ),
      axios.get(`${italyDevUrl}scenarioIDs`),
      axios.get(`${italyDevUrl}metricIDs`),
    ]);
    const updatedState = {};
    scenarios.forEach((scenario) => (updatedState[scenario] = false));
    updatedState.data = dataResponse.data;
    updatedState.scenarioIDs = scenariosResponse.data;
    updatedState.metricIDs = metricsResponse.data;

    this.setState(updatedState);
  }

  async componentDidUpdate(prevProps) {
    const { state, scenarios } = this.props;
    const { frequencyValue, selectedScenarios } = this.state;

    const activeScenarios = Object.keys(selectedScenarios)
      .filter((k) => selectedScenarios[k])
      .map((v) => `"${v}"`)
      .join(",");

    if (prevProps.state !== state && activeScenarios.length) {
      this.setState({ loading: true }, () => {
        axios
          .get(
            `${italyDevUrl}data?state=${state}&frequency=${frequencyValue}&selectedScenarios=[${activeScenarios}]`
          )
          .then((result) =>
            this.setState({
              loading: false,
              data: result.data,
            })
          );
      });
    }

    if (prevProps.scenarios !== scenarios) {
      const updatedState = {
        selectedScenarios: {},
      };
      scenarios.forEach((scenario) => {
        if (scenario === "Lockdown kept indefinitely") {
          updatedState["selectedScenarios"][scenario] = true;
        } else {
          updatedState["selectedScenarios"][scenario] = false;
        }
      });
      this.setState(updatedState);
    }
  }

  _handleScenarioChange = async (e) => {
    const { state } = this.props;
    const { frequencyValue } = this.state;

    const selectedScenarios = { ...this.state.selectedScenarios };
    selectedScenarios[e.target.name] = e.target.checked;

    const activeScenarios = Object.keys(selectedScenarios)
      .filter((k) => selectedScenarios[k])
      .map((v) => `"${v}"`)
      .join(",");

    if (activeScenarios === "") {
      this.setState({
        selectedScenarios,
        data: [],
      });
    } else {
      this.setState({ loading: true });

      const response = await axios.get(
        `${italyDevUrl}data?state=${state}&frequency=${frequencyValue}&selectedScenarios=[${activeScenarios}]`
      );
      this.setState({ selectedScenarios, data: response.data, loading: false });
    }
  };

  _handleMetricChange = (e) => {
    this.setState({ ...this.state, [e.target.name]: e.target.checked });
  };

  _handleFrequencyChange = async (e) => {
    const { state } = this.props;
    const { selectedScenarios } = this.state;
    const activeScenarios = Object.keys(selectedScenarios)
      .filter((k) => selectedScenarios[k])
      .map((v) => `"${v}"`)
      .join(",");
    const frequency = e.target.value;
    if (activeScenarios.length) {
      this.setState({ loading: true }, () => {
        axios
          .get(
            `${italyDevUrl}data?state=${state}&frequency=${frequency}&selectedScenarios=[${activeScenarios}]`
          )
          .then((result) =>
            this.setState({
              loading: false,
              data: result.data,
              frequencyValue: frequency,
            })
          );
      });
    } else {
      this.setState({
        frequencyValue: frequency,
      });
    }
  };

  getDataKeys = () => {
    // nonsense we have to do for recharts
    const {
      infections,
      newDeaths,
      frequencyValue,
      selectedScenarios,
      metricIDs,
      scenarioIDs,
    } = this.state;
    const dataKeys = [];
    const isWeekly = frequencyValue === "weekly";

    const scenarios = Object.keys(selectedScenarios).filter(
      (key) => selectedScenarios[key]
    );

    if (infections) {
      scenarios.forEach((scenario) => {
        let key = isWeekly
          ? `${metricIDs["Total Infectious per 10,000"]}|${scenarioIDs[scenario]}_weekly`
          : `${metricIDs["Total Infectious per 10,000"]}|${scenarioIDs[scenario]}`;
        dataKeys.push(key);
      });
    }

    if (newDeaths) {
      scenarios.forEach((scenario) => {
        let key = isWeekly
          ? `${metricIDs["New Deaths"]}|${scenarioIDs[scenario]}_weekly`
          : `${metricIDs["New Deaths"]}|${scenarioIDs[scenario]}`;
        dataKeys.push(key);
      });
    }

    return dataKeys;
  };

  // these are both hacky af but i'm tired

  legendLabels = {
    "Total Infectious per 10,000|Mitigated":
      "Total Infections per 10,000|Mitigated",
    "Total Infectious per 10,000|Unmitigated":
      "Total Infections per 10,000|Unmitigated",
  };

  tickLabels = {
    "01": "1",
    "02": "2",
    "03": "3",
    "04": "4",
    "05": "5",
    "06": "6",
    "07": "7",
    "08": "8",
    "09": "9",
    "10": "10",
    "11": "11",
    "12": "12",
  };

  kFormat = (tickItem) => {
    // format thousands into K, and millions into M
    return Math.abs(tickItem) > 999 && Math.abs(tickItem) < 999999
      ? Math.sign(tickItem) * (Math.abs(tickItem) / 1000).toFixed(1) + "K"
      : Math.abs(tickItem) > 999999
      ? Math.sign(tickItem) * (Math.abs(tickItem) / 1000000).toFixed(1) + "M"
      : tickItem === 0 || tickItem >= 0.01
      ? tickItem
      : tickItem.toFixed(5);
  };

  getTruncatedLabel = (label) => {
    const labels = {
      "No Lockdown": "No Lockdown",
      "Lockdown kept indefinitely": "Lockdown kept indefinitely",
      "Lift 1 (May 4th): 50% work sector; 10% community; schools closed; 50% mobility":
        "Lift 1 (50% Mobility)",
      "Lift 2 (May 4th): 70% work sector; 50% community; schools closed; 50% mobility":
        "Lift 2 (50% Mobility)",
      "Lift 1 (May 4th): 50% work sector; 10% community; schools closed; 100% mobility":
        "Lift 1 (100% Mobility)",
      "Lift 2 (May 4th): 70% work sector; 50% community; schools closed; 100% mobility":
        "Lift 2 (100% Mobility)",
      "Full Lift (May 4th): social distancing measures are lifted. Full mobility resumed. Schools and education activities are resumed":
        "Full Lift",
      "Schools Closed (May 4th): lockdown lifted, schools remain close, 70% mobility":
        "Schools Closed",
      "Lift Aware": "Lift Aware",
      "Lift Aware Seasonal": "Lift Aware Seasonal",
      "Normal Aware": "Normal Aware",
      "Normal Aware Seasonal": "Normal Aware Seasonal",
    };
    return labels[label] || label;
  };

  getKeyByValue = (object, value) => {
    return Object.keys(object).find(
      (key) => String(object[key]) === String(value)
    );
  };

  getLineName = (key) => {
    // formatting for tooltip / legend based on frequency value
    const { metricIDs, scenarioIDs } = this.state;
    const metric = key.split("|")[0];
    const metricLabel = this.getKeyByValue(metricIDs, metric);

    if (this.state.frequencyValue === "daily") {
      const scenario = key.split("|")[1];
      const scenarioLabel = this.getKeyByValue(scenarioIDs, scenario);
      return `${
        this.legendLabels[metricLabel] || metricLabel
      }, ${this.getTruncatedLabel(scenarioLabel)} (Mean)`;
    }

    const scenario = key.split("|")[1].split("_")[0];
    const scenarioLabel = this.getKeyByValue(scenarioIDs, scenario);

    return `${
      this.legendLabels[metricLabel] || metricLabel
    }, ${this.getTruncatedLabel(scenarioLabel)} (Mean)`;
  };

  // scenarioIndices = {
  //   0: "No Lockdown",
  //   1: "Lockdown kept indefinitely",
  //   2: "Lift 1 (May 4th): 50% work sector; 10% community; schools closed; 50% mobility",
  //   3: "Lift 1 (May 4th): 50% work sector; 10% community; schools closed; 100% mobility",
  //   4: "Lift 2 (May 4th): 70% work sector; 50% community; schools closed; 50% mobility",
  //   5: "Lift 2 (May 4th): 70% work sector; 50% community; schools closed; 100% mobility",
  //   6: "Schools Closed (May 4th): lockdown lifted, schools remain close, 70% mobility",
  //   7: "Full Lift (May 4th): social distancing measures are lifted. Full mobility resumed. Schools and education activities are resumed",
  // };

  scenarioIndices = {
    0: "Full Lift (May 4th): social distancing measures are lifted. Full mobility resumed. Schools and education activities are resumed",
    1: "Lift 1 (May 4th): 50% work sector; 10% community; schools closed; 100% mobility",
    2: "Lift 1 (May 4th): 50% work sector; 10% community; schools closed; 50% mobility",
    3: "Lift 2 (May 4th): 70% work sector; 50% community; schools closed; 100% mobility",
    4: "Lift 2 (May 4th): 70% work sector; 50% community; schools closed; 50% mobility",
    5: "Lift Aware",
    6: "Lift Aware Seasonal",
    7: "Lockdown kept indefinitely",
    8: "No Lockdown",
    9: "Normal Aware",
    10: "Normal Aware Seasonal",
    11: "Schools Closed (May 4th): lockdown lifted, schools remain close, 70% mobility",
  };

  render() {
    const { state, scenarios } = this.props;
    const { data, scaleValue, frequencyValue } = this.state;
    const dataKeys = this.getDataKeys();
    const dateToday = format(new Date(), "yyyy-MM-dd").toString();

    // filter out 0s for log scale
    let chartData = [];
    if (this.state.scaleValue === "Logarithmic") {
      chartData = data.filter((row) => {
        const keys = Object.keys(row);
        let returnVal = true;
        keys.forEach((key) => {
          if (key.includes("amt") || key.includes("range")) {
            if (row[key] === 0) {
              returnVal = false;
            } else if (Array.isArray(row[key])) {
              if (row[key][0] === 0 || row[key[1]] === 0) {
                returnVal = false;
              }
            }
          }
        });
        return returnVal;
      });
    } else {
      chartData = data;
    }

    return (
      <Container
        maxWidth={false}
        disableGutters
        className="chartGrid"
        id="infdthproj"
      >
        <Sticky enabled top={0} bottomBoundary="#icubedproj">
          <Grid container style={{ paddingTop: 15 }}>
            <Grid item xs={12} sm={1}></Grid>
            <Grid item xs={12} sm={3}>
              <FormControl
                fullWidth
                className="form-control"
                style={{ paddingBottom: "15px" }}
              >
                <InputLabel
                  htmlFor="filled-age-native-simple"
                  style={{ color: "#2a383c", opacity: 0.3 }}
                >
                  SET LOCATION:
                </InputLabel>
                <Select
                  native
                  inputProps={{
                    name: "age",
                    id: "filled-age-native-simple",
                  }}
                  onChange={(e) => this.props.handleStateChange(e.target.value)}
                >
                  {this.props.availableStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs sm={8}></Grid>
          </Grid>
        </Sticky>
        <Grid container spacing={0} style={{ paddingTop: 0 }}>
          <Grid item xs></Grid>
          <Grid item xs={12} sm={10}>
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <h2>Infection and Death Projections for {state}</h2>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" style={{ fontSize: 14 }}>
                        Scale
                      </FormLabel>
                      <RadioGroup
                        aria-label="scale"
                        name="scale"
                        id="scaleFilter"
                        value={scaleValue}
                        onChange={(e) =>
                          this.setState({
                            scaleValue: e.target.value,
                          })
                        }
                      >
                        <FormControlLabel
                          value="Linear"
                          control={<Radio />}
                          label="Linear"
                        />
                        <FormControlLabel
                          value="Logarithmic"
                          control={<Radio />}
                          label="Logarithmic"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControl component="fieldset" fullWidth>
                      <FormLabel component="legend" style={{ fontSize: 14 }}>
                        Frequency
                      </FormLabel>
                      <RadioGroup
                        aria-label="frequency"
                        name="frequency"
                        id="frequencyFilter"
                        value={frequencyValue}
                        onChange={this._handleFrequencyChange}
                      >
                        <FormControlLabel
                          value="weekly"
                          control={<Radio />}
                          label="Weekly"
                        />
                        <FormControlLabel
                          value="daily"
                          control={<Radio />}
                          label="Daily"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={9}>
                {this.state.loading ? (
                  <div>
                    <LinearProgress />
                    <h4 style={{ textAlign: "center" }}>Loading data ... </h4>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={600}>
                    <ComposedChart
                      margin={{
                        top: 10,
                        right: 60,
                        left: 0,
                        bottom: 80,
                      }}
                      data={chartData}
                    >
                      <CartesianGrid stroke="#f5f5f5" />
                      <XAxis
                        label={{
                          value:
                            this.state.frequencyValue === "daily"
                              ? "Date"
                              : "Epiweek",
                          position: "bottom",
                          dy: 10,
                          fontSize: 16,
                        }}
                        interval={
                          this.state.frequencyValue === "daily"
                            ? "preserveEnd"
                            : 2
                        }
                        tickFormatter={(tick) => {
                          if (this.state.frequencyValue === "weekly") {
                            return tick;
                          }
                          const parts = tick.split("-");
                          return `${this.tickLabels[parts[1]]}/${parts[2]}`;
                        }}
                        dy={10}
                        dataKey={
                          this.state.frequencyValue === "daily"
                            ? "date"
                            : "epi_week"
                        }
                        tick={{ fontSize: 14 }}
                        padding={{ left: 0, right: 0 }}
                        allowDuplicatedCategory={false}
                      />
                      <YAxis
                        tick={{ fontSize: 14 }}
                        scale={scaleValue === "Logarithmic" ? "log" : "auto"}
                        domain={["auto", "auto"]}
                        label={
                          <Text
                            x={0}
                            y={0}
                            dx={15}
                            dy={250}
                            offset={0}
                            angle={-90}
                            fontSize={16}
                          >
                            Number Affected
                          </Text>
                        }
                        tickFormatter={(tick) => {
                          return this.kFormat(tick);
                        }}
                      />
                      <Tooltip
                        cursor={{ stroke: "#4c5b5f", strokeWidth: 1 }}
                        itemStyle={{
                          fontSize: 12,
                          padding: "0 0 3px 5px",
                          margin: 0,
                          borderLeft: "3px solid",
                        }}
                        formatter={(value, name) => {
                          if (Array.isArray(value)) {
                            return [
                              `[${value[0].toLocaleString()} - ${value[1].toLocaleString()}]`,
                              name,
                            ];
                          }
                          return [value.toLocaleString(), name];
                        }}
                        labelStyle={{
                          fontWeight: 600,
                          marginLeft: -20,
                          marginBottom: 10,
                          fontSize: 16,
                        }}
                      />
                      {data.length && (
                        <Legend
                          wrapperStyle={{
                            fontSize: 13,
                            paddingTop: 45,
                            paddingLeft: 10,
                          }}
                        />
                      )}

                      {dataKeys.map((key) => [
                        <Line
                          name={this.getLineName(key)}
                          type="monotone"
                          dataKey={`amt_${key}`}
                          stroke={COLORS[dataKeys.indexOf(key)]}
                          strokeWidth={3}
                          fill={COLORS[dataKeys.indexOf(key)]}
                          connectNulls
                          dot={false}
                          activeDot={{ stroke: "white", strokeWidth: 2, r: 9 }}
                        />,
                        <Area
                          name="Projection Range"
                          type="monotone"
                          dataKey={`range_${key}`}
                          stroke={COLORS[dataKeys.indexOf(key)]}
                          fill={COLORS[dataKeys.indexOf(key)]}
                          fillOpacity="0.2"
                          strokeOpacity="0.6"
                          strokeDasharray="3 3"
                          connectNulls
                          dot={false}
                          activeDot={false}
                          legendType="none"
                        />,
                      ])}
                      {this.state.frequencyValue === "weekly" &&
                      this.state.newDeaths ? (
                        <Scatter
                          dataKey="new_deaths"
                          name="Surveillance Data"
                          fill="#7CE4BB"
                          stroke="#ffffff"
                        />
                      ) : null}
                      {this.state.frequencyValue === "daily" ? (
                        <ReferenceLine
                          x={dateToday}
                          label={{
                            value: "Today",
                            fill: "#7F7F7F",
                            position: "insideTopLeft",
                            fontSize: 12,
                            dy: 10,
                            fontWeight: 600,
                          }}
                          stroke="#7F7F7F"
                          strokeDasharray="5 5"
                          ifOverflow="extendDomain"
                        />
                      ) : null}
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </Grid>
              <Grid item xs={12} sm={3}>
                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Metric</FormLabel>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={this.state.infections}
                            onChange={this._handleMetricChange}
                            name="infections"
                          />
                        }
                        label="Total Infections per 10,000"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={this.state.newDeaths}
                            onChange={this._handleMetricChange}
                            name="newDeaths"
                          />
                        }
                        label="New Deaths"
                      />
                    </FormGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">Scenario</FormLabel>
                    <FormGroup>
                      {scenarios
                        .map((scenario, index) => {
                          // console.log(scenario);
                          return this.scenarioIndices[index] || scenario;
                        })
                        .map((scenario) => {
                          // if (
                          //   scenario &&
                          //   scenario === "No Lockdown" &&
                          //   state !== "Italy"
                          // )
                          //   return null;
                          return (
                            <FormControlLabel
                              key={scenario}
                              style={{ padding: "5px" }}
                              control={
                                <Checkbox
                                  checked={
                                    this.state["selectedScenarios"][scenario] ||
                                    false
                                  }
                                  onChange={this._handleScenarioChange}
                                  name={scenario}
                                />
                              }
                              label={scenario}
                            />
                          );
                        })}
                    </FormGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs></Grid>
        </Grid>
      </Container>
    );
  }
}

export default Charts;
